import {
  TEXT_EDITOR_A4_HEIGHT_PX,
  TEXT_EDITOR_A4_WIDTH_PX,
  TEXT_EDITOR_PAGE_ORIENTATION,
  getTextEditorPageLayoutMetrics,
  normalizeTextEditorPageOrientation,
} from './textEditorPageOrientation';

import {
  normalizeCustomPrintMarginsIn,
  normalizePrintMarginPreset,
  resolveTextEditorPrintMargins,
  TEXT_EDITOR_DEFAULT_CUSTOM_PRINT_MARGINS_IN,
  TEXT_EDITOR_DEFAULT_PRINT_MARGIN_PRESET,
  TEXT_EDITOR_PRINT_MARGIN_PRESET,
} from './textEditorPrintLayout';

export const TEXT_EDITOR_PAGE_LAYOUT_MODE = {
  PAGINATED: 'paginated',
  CONTINUOUS: 'continuous',
};

export const TEXT_EDITOR_DEFAULT_PAGE_LAYOUT_MODE = TEXT_EDITOR_PAGE_LAYOUT_MODE.PAGINATED;

/** Gap between stacked page previews (px). */
export const TEXT_EDITOR_PAGE_SHEET_GAP_PX = 12;

/** Footer band reserved for page numbers inside bottom margin (px). */
export const TEXT_EDITOR_PAGE_NUMBER_BAND_PX = 36;

export function getPageSheetVerticalInsetPx(margins) {
  const m = margins || resolveTextEditorPrintMargins({});
  return m.topPx + m.bottomPx;
}

export function getPageSheetHorizontalInsetPx(margins) {
  const m = margins || resolveTextEditorPrintMargins({});
  return m.leftPx + m.rightPx;
}

/** Scroll height of one logical page of body content (for splitting). */
export const TEXT_EDITOR_DEFAULT_PRINT_PAGE_NUMBERS_ENABLED = true;

export function getTextEditorPageContentStepPx(orientation, margins, pageNumbers = true) {
  const sheet = getTextEditorPageSheetOuterHeightPx(orientation);
  const verticalInset = getPageSheetVerticalInsetPx(margins);
  const numberBand = pageNumbers ? TEXT_EDITOR_PAGE_NUMBER_BAND_PX : 0;
  return Math.max(320, sheet - verticalInset - numberBand);
}

export function countTextEditorDocumentPages(contentScrollHeightPx, orientation, margins, pageNumbers = true) {
  const step = getTextEditorPageContentStepPx(orientation, margins, pageNumbers);
  const height = Math.max(0, Number(contentScrollHeightPx) || 0);
  return Math.max(1, Math.ceil(height / step));
}

export function normalizeTextEditorPageLayoutMode(value) {
  return value === TEXT_EDITOR_PAGE_LAYOUT_MODE.CONTINUOUS
    ? TEXT_EDITOR_PAGE_LAYOUT_MODE.CONTINUOUS
    : TEXT_EDITOR_PAGE_LAYOUT_MODE.PAGINATED;
}

export function getTextEditorPageSheetOuterHeightPx(orientation) {
  const metrics = getTextEditorPageLayoutMetrics(orientation);
  return metrics.heightPx;
}

export function getTextEditorPageSheetOuterWidthPx(orientation) {
  const metrics = getTextEditorPageLayoutMetrics(orientation);
  return metrics.widthPx;
}

/**
 * Uniform scale so one A4 sheet fits in the available preview area (aspect ratio preserved).
 * @param {number} capAtOne When true, never upscale beyond 100% (print-accurate px).
 * @param {boolean} fitViewportHeight When false, only width constrains scale (desktop: full A4 width, scroll vertically).
 */
export function getTextEditorPagePreviewScale(
  availableWidthPx,
  availableHeightPx,
  orientation,
  capAtOne = true,
  fitViewportHeight = true
) {
  const sheetW = getTextEditorPageSheetOuterWidthPx(orientation);
  const sheetH = getTextEditorPageSheetOuterHeightPx(orientation);
  const pad = 12;
  const w = Math.max(0, Number(availableWidthPx) - pad);
  const h = Math.max(0, Number(availableHeightPx) - pad);
  if (w <= 0 || sheetW <= 0 || sheetH <= 0) return 1;
  if (fitViewportHeight && h <= 0) return 1;
  let scale = w / sheetW;
  if (fitViewportHeight) {
    scale = Math.min(scale, h / sheetH);
  }
  if (capAtOne) scale = Math.min(1, scale);
  return Math.max(0.2, scale);
}

export function shouldIncludePageNumbersInExport(pageLayoutMode, printPageNumbersEnabled) {
  const isPaginated =
    normalizeTextEditorPageLayoutMode(pageLayoutMode) === TEXT_EDITOR_PAGE_LAYOUT_MODE.PAGINATED;
  if (!isPaginated) return false;
  if (printPageNumbersEnabled === false) return false;
  return true;
}

export function resolveTextEditorPageExportSettings({
  pageOrientation,
  pageLayoutMode,
  printMarginPreset,
  customPrintMarginsIn,
  printPageNumbersEnabled = TEXT_EDITOR_DEFAULT_PRINT_PAGE_NUMBERS_ENABLED,
} = {}) {
  const orientation = normalizeTextEditorPageOrientation(pageOrientation);
  const layoutMode = normalizeTextEditorPageLayoutMode(pageLayoutMode);
  const pageNumbers = shouldIncludePageNumbersInExport(layoutMode, printPageNumbersEnabled);
  const margins = resolveTextEditorPrintMargins({
    preset: normalizePrintMarginPreset(printMarginPreset),
    customMarginsIn: normalizeCustomPrintMarginsIn(customPrintMarginsIn),
    pageNumbers,
  });
  return {
    pageOrientation: orientation,
    pageLayoutMode: layoutMode,
    pageNumbers,
    printMarginPreset: margins.preset,
    customPrintMarginsIn: normalizeCustomPrintMarginsIn(customPrintMarginsIn),
    printMargins: margins,
  };
}

export {
  TEXT_EDITOR_DEFAULT_CUSTOM_PRINT_MARGINS_IN,
  TEXT_EDITOR_DEFAULT_PRINT_MARGIN_PRESET,
  TEXT_EDITOR_PRINT_MARGIN_PRESET,
};

export { TEXT_EDITOR_A4_HEIGHT_PX, TEXT_EDITOR_A4_WIDTH_PX, TEXT_EDITOR_PAGE_ORIENTATION };
